import React from 'react';
import { useTranslation } from 'react-i18next';
import './IvrOptionDistributionWidget.css';

/**
 * IvrOptionDistributionWidget — répartition des entrées d'un menu IVR (KPI ivr-statistics).
 * Une barre 100% par menu : segments = touches tapées (USER_INPUT) + buckets spéciaux
 * Invalide / Timeout / Abandon. Lignes groupées par flow. Touches dynamiques → couleurs
 * stables (palette pour les digits, couleurs fixes neutres/rouge pour les spéciaux).
 */
// Palette catégorielle ancrée sur le thème Heedify (navy / teal / gold + dérivées harmonieuses).
const PALETTE = [
    '#1B3A6B', '#00A4A6', '#FFC700', '#6C4FA1', '#22497F', '#008E90',
    '#FF8C00', '#4A7BB0', '#3FA796', '#B5546F', '#8C6D1F', '#5A6BA8'
];
const SPECIAL = {
    __invalid: { key: 'INVALID', color: '#8A8886' },
    __timeout: { key: 'TIMEOUT', color: '#C8C6C4' },
    __abandon: { key: 'ABANDON_IVR', color: '#D13438' },
    __other: { key: 'OTHER', color: '#D2D0CE' }
};

const IvrOptionDistributionWidget = ({ data, config }) => {
    const { t } = useTranslation();

    if (!Array.isArray(data) || data.length === 0) {
        return null;
    }

    const fmtPct = (n) => {
        const v = Number(n);
        return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}%`;
    };
    const fmtInt = (v) => (v === null || v === undefined ? '-' : Number(v).toLocaleString());

    // Union des touches (ordre numérique si possible) + map de couleurs déterministe.
    const digitSet = new Set();
    data.forEach((r) => Object.keys(r.options || {}).forEach((d) => digitSet.add(d)));
    const digits = Array.from(digitSet).sort((a, b) => {
        const na = Number(a), nb = Number(b);
        if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
        return String(a).localeCompare(String(b));
    });
    const colorOf = {};
    digits.forEach((d, i) => { colorOf[d] = PALETTE[i % PALETTE.length]; });

    // Groupement par flow.
    const groups = [];
    const idx = {};
    data.forEach((r) => {
        const k = r.flowName || '—';
        if (idx[k] === undefined) { idx[k] = groups.length; groups.push({ flowName: k, rows: [] }); }
        groups[idx[k]].rows.push(r);
    });

    const segmentsFor = (r) => {
        const entries = Number(r.entries) || 0;
        const segs = [];
        Object.entries(r.options || {}).forEach(([d, n]) => {
            if (Number(n) > 0) segs.push({ label: d, color: colorOf[d], count: Number(n) });
        });
        segs.sort((a, b) => digits.indexOf(a.label) - digits.indexOf(b.label));
        const inv = Number(r.invalidInputs) || 0;
        const to = Number(r.timeouts) || 0;
        const ab = Number(r.abandons) || 0;
        if (inv > 0) segs.push({ label: t(SPECIAL.__invalid.key), color: SPECIAL.__invalid.color, count: inv });
        if (to > 0) segs.push({ label: t(SPECIAL.__timeout.key), color: SPECIAL.__timeout.color, count: to });
        if (ab > 0) segs.push({ label: t(SPECIAL.__abandon.key), color: SPECIAL.__abandon.color, count: ab });
        const known = segs.reduce((acc, s) => acc + s.count, 0);
        const other = entries - known;
        if (other > 0) segs.push({ label: t(SPECIAL.__other.key), color: SPECIAL.__other.color, count: other });
        const total = segs.reduce((acc, s) => acc + s.count, 0) || 1;
        return segs.map((s) => ({ ...s, pct: (s.count / total) * 100 }));
    };

    return (
        <div className="ivr-od-widget">
            <div className="ivr-od-legend">
                {digits.map((d) => (
                    <span className="ivr-od-legend-item" key={d}>
                        <span className="ivr-od-swatch" style={{ background: colorOf[d] }} />
                        {d}
                    </span>
                ))}
                <span className="ivr-od-legend-item"><span className="ivr-od-swatch" style={{ background: SPECIAL.__invalid.color }} />{t('INVALID')}</span>
                <span className="ivr-od-legend-item"><span className="ivr-od-swatch" style={{ background: SPECIAL.__timeout.color }} />{t('TIMEOUT')}</span>
                <span className="ivr-od-legend-item"><span className="ivr-od-swatch" style={{ background: SPECIAL.__abandon.color }} />{t('ABANDON_IVR')}</span>
            </div>
            <div className="ivr-od-scroll">
                {groups.map((g, gi) => (
                    <div className="ivr-od-group" key={gi}>
                        <div className="ivr-od-flow">{g.flowName}</div>
                        {g.rows.map((r, ri) => {
                            const segs = segmentsFor(r);
                            return (
                                <div className="ivr-od-row" key={ri}>
                                    <div className="ivr-od-menu" title={r.ivrName}>{r.ivrName || '-'}</div>
                                    <div className="ivr-od-bar">
                                        {segs.map((seg, si) => (
                                            <div
                                                key={si}
                                                className="ivr-od-seg"
                                                style={{ width: `${seg.pct}%`, background: seg.color }}
                                                title={`${seg.label}: ${fmtInt(seg.count)} (${fmtPct(seg.pct)})`}
                                            >
                                                {seg.pct >= 12 ? <span className="ivr-od-seg-label">{seg.label} {fmtPct(seg.pct)}</span> : null}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="ivr-od-entries">{fmtInt(r.entries)}</div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default IvrOptionDistributionWidget;
